/**
 * /terceirizacao/parceiros — CRUD de Parceiros de terceirizacao (Fase 10).
 *
 * Lista + criar/editar via SlideOverPanel.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiEdit2,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarParceiro,
  CATEGORIAS,
  CATEGORIA_LABELS,
  criarParceiro,
  desativarParceiro,
  listarParceiros,
} from "../../../services/terceirizacao";
import { PageContainer } from "../../../styles/shared";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";


function ParceiroForm({ inicial, onSave, onCancel }) {
  const [dados, setDados] = useState({
    nome: inicial?.nome || "",
    cnpj_cpf: inicial?.cnpj_cpf || "",
    categoria: inicial?.categoria || "LAQUEACAO",
    sla_dias: inicial?.sla_dias || "7.0",
    telefone: inicial?.telefone || "",
    email: inicial?.email || "",
    endereco: inicial?.endereco || "",
    observacoes: inicial?.observacoes || "",
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Nome *</span>
        <Input
          value={dados.nome}
          onChange={(e) => setDados({ ...dados, nome: e.target.value })}
          maxLength={160}
          required
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>CNPJ / CPF</span>
        <Input
          value={dados.cnpj_cpf}
          onChange={(e) => setDados({ ...dados, cnpj_cpf: e.target.value })}
          maxLength={20}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Categoria *</span>
        <Select
          value={dados.categoria}
          onChange={(e) => setDados({ ...dados, categoria: e.target.value })}
        >
          {CATEGORIAS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </Select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>SLA (dias) *</span>
        <Input
          type="number"
          step="0.1"
          min="0.5"
          max="60"
          value={dados.sla_dias}
          onChange={(e) => setDados({ ...dados, sla_dias: e.target.value })}
        />
        <small style={{ opacity: 0.6 }}>
          Prazo padrão para retorno após envio (ex: 5.0 = 5 dias)
        </small>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Telefone</span>
        <Input
          value={dados.telefone}
          onChange={(e) => setDados({ ...dados, telefone: e.target.value })}
          maxLength={30}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Email</span>
        <Input
          type="email"
          value={dados.email}
          onChange={(e) => setDados({ ...dados, email: e.target.value })}
          maxLength={120}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Endereço</span>
        <Input
          value={dados.endereco}
          onChange={(e) => setDados({ ...dados, endereco: e.target.value })}
          maxLength={300}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Observações</span>
        <Textarea
          rows={2}
          value={dados.observacoes}
          onChange={(e) => setDados({ ...dados, observacoes: e.target.value })}
        />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
        <AsyncButton
          variant="primary"
          onClick={() => onSave(dados)}
          successMessage="Parceiro salvo"
        >
          Salvar
        </AsyncButton>
      </div>
    </div>
  );
}


export default function ParceirosLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [editando, setEditando] = useState(null); // null | "novo" | objeto
  const [unidadeId, setUnidadeId] = useState("");

  const podeManage = isMaster || hasAllPermissions(["parceiro:manage"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarParceiros(request, {
        categoria: filtroCategoria || undefined,
      });
      const list = Array.isArray(payload) ? payload : payload?.results || [];
      setItems(list);
      // Pega unidade do primeiro parceiro para usar em criar novo
      if (list.length > 0 && !unidadeId) {
        setUnidadeId(list[0].unidade);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar parceiros.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [request, filtroCategoria, unidadeId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Tenta carregar a unidade default se ainda nao tem (caso lista vazia)
  useEffect(() => {
    if (unidadeId) return;
    // Fallback: chama /api/unidades e pega o primeiro
    request("/api/unidades")
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.results || [];
        if (list.length > 0) setUnidadeId(list[0].id);
      })
      .catch(() => {/* silencioso */});
  }, [request, unidadeId]);

  const handleSalvar = async (dados) => {
    if (editando === "novo") {
      if (!unidadeId) {
        throw new Error("Unidade não identificada.");
      }
      await criarParceiro(request, { ...dados, unidade: unidadeId });
    } else {
      await atualizarParceiro(request, editando.id, dados);
    }
    setEditando(null);
    await carregar();
  };

  const handleExcluir = async (parceiro) => {
    if (!window.confirm(`Desativar parceiro "${parceiro.nome}"?`)) return;
    await desativarParceiro(request, parceiro.id);
    await carregar();
  };

  const kpis = (() => {
    const ativos = items.filter((p) => p.ativo);
    const inativos = items.filter((p) => !p.ativo);
    return [
      {
        label: "Total cadastrados",
        value: items.length,
        tone: "neutral",
      },
      {
        label: "Ativos",
        value: ativos.length,
        tone: ativos.length > 0 ? "success" : "warning",
      },
      {
        label: "Inativos",
        value: inativos.length,
        tone: "neutral",
      },
    ];
  })();

  return (
    <PageContainer>
      <EntityHeader
        title="Parceiros de terceirização"
        subtitle="Cadastro de laqueadores, vidraceiros, soldadores, marmorarias (ADR-0025)"
        actions={[
          {
            label: "Voltar",
            icon: <FiArrowLeft size={14} />,
            onClick: () => navigate("/terceirizacao/oses"),
          },
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} />,
            onClick: carregar,
          },
          ...(podeManage
            ? [{
                label: "Novo parceiro",
                icon: <FiPlus size={14} />,
                onClick: () => setEditando("novo"),
                primary: true,
              }]
            : []),
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 240 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Categoria</span>
          <Select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
          >
            <option value="">Todas</option>
            {CATEGORIAS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>
        </label>
      </Card>

      {error && <ErrorMsg><FiAlertTriangle size={14} /> {error}</ErrorMsg>}

      <Card>
        <SectionTitle>
          {loading ? "Carregando..." : `${items.length} parceiro(s)`}
        </SectionTitle>
        {!loading && items.length === 0 && (
          <EmptyMsg>Nenhum parceiro cadastrado.</EmptyMsg>
        )}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Categoria</th>
                  <th>SLA</th>
                  <th>Contato</th>
                  <th>Status</th>
                  {podeManage && <th>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.nome}</strong>
                      {p.cnpj_cpf && (
                        <div style={{ fontSize: 12, opacity: 0.6 }}>{p.cnpj_cpf}</div>
                      )}
                    </td>
                    <td>{CATEGORIA_LABELS[p.categoria] || p.categoria}</td>
                    <td>{p.sla_dias}d</td>
                    <td>
                      <div style={{ fontSize: 13 }}>{p.telefone}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{p.email}</div>
                    </td>
                    <td>
                      <Pill $tone={p.ativo ? "success" : "neutral"}>
                        {p.ativo ? "Ativo" : "Inativo"}
                      </Pill>
                    </td>
                    {podeManage && (
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <SecondaryButton
                            onClick={() => setEditando(p)}
                            style={{ padding: "4px 8px", fontSize: 12 }}
                          >
                            <FiEdit2 size={12} /> Editar
                          </SecondaryButton>
                          {p.ativo && (
                            <SecondaryButton
                              onClick={() => handleExcluir(p)}
                              style={{ padding: "4px 8px", fontSize: 12 }}
                            >
                              <FiTrash2 size={12} /> Desativar
                            </SecondaryButton>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {editando && (
        <SlideOverPanel
          open
          onClose={() => setEditando(null)}
          title={editando === "novo" ? "Novo parceiro" : `Editar ${editando.nome}`}
        >
          <ParceiroForm
            inicial={editando === "novo" ? null : editando}
            onSave={handleSalvar}
            onCancel={() => setEditando(null)}
          />
        </SlideOverPanel>
      )}
    </PageContainer>
  );
}
